import React, { useEffect, useRef, useState } from 'react';
import { Book, Users, BookOpen, Smartphone, Mail, Search, ArrowRight } from 'lucide-react';

const ServicesFeatures = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const features = [
    {
      icon: <Book className="w-7 h-7" />,
      title: "Dedicated Book Pages",
      description: "Elegant showcase pages for each title with seamless purchase links to Amazon, Barnes & Noble, and all major platforms.",
      accent: "Each book deserves its own stage"
    },
    {
      icon: <Users className="w-7 h-7" />,
      title: "Members-Only Areas",
      description: "Exclusive content sanctuaries for your devoted readers — bonus chapters, behind-the-scenes glimpses, and early access privileges.",
      accent: "Reward your loyal readers"
    },
    {
      icon: <BookOpen className="w-7 h-7" />,
      title: "Chapter Previews",
      description: "Beautifully formatted HTML preview sections that invite readers into your world while protecting your creative work.",
      accent: "Let your words speak first"
    },
    {
      icon: <Smartphone className="w-7 h-7" />,
      title: "Responsive Excellence",
      description: "Meticulously crafted designs that perform flawlessly across all devices, from intimate mobile reading to desktop discovery.",
      accent: "Perfect on every screen"
    },
    {
      icon: <Search className="w-7 h-7" />,
      title: "Search Optimization",
      description: "Strategic SEO architecture that helps new readers discover your books organically through search engines.",
      accent: "Be found by the right readers"
    },
    {
      icon: <Mail className="w-7 h-7" />,
      title: "Newsletter Integration",
      description: "Sophisticated subscriber management and automated engagement tools to nurture your growing community of readers.",
      accent: "Build lasting connections"
    }
  ];

  return (
    <section ref={sectionRef} className="relative py-24 lg:py-32 bg-ink-900 overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        {/* Subtle Pattern */}
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='1' fill-rule='evenodd'%3E%3Cpath d='M0 40L40 0H20L0 20M40 40V20L20 40'/%3E%3C/g%3E%3C/svg%3E")`
        }} />

        {/* Decorative Glow */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-gold-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-gold-400/5 rounded-full blur-3xl" />
      </div>

      {/* Gold Accent Lines */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold-500/30 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold-500/30 to-transparent" />

      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        {/* Header */}
        <div className={`text-center max-w-3xl mx-auto mb-20 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {/* Section Label */}
          <div className="inline-flex items-center gap-3 mb-6">
            <span className="w-8 h-px bg-gold-500" />
            <span className="text-gold-400 text-sm font-sans tracking-[0.2em] uppercase">
              What We Offer
            </span>
            <span className="w-8 h-px bg-gold-500" />
          </div>

          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-cream-50 mb-6">
            Everything Your Author
            <span className="block mt-2 text-gradient-gold">Platform Needs</span>
          </h2>

          <p className="font-body text-lg text-cream-300 leading-relaxed max-w-2xl mx-auto">
            We employ modern technologies like React and Node.js to create high-performance,
            scalable websites that are both visually stunning and highly functional.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {features.map((feature, index) => (
            <div
              key={index}
              className={`group relative p-8 lg:p-10 bg-ink-800/50 border border-ink-700/50 backdrop-blur-sm transition-all duration-700 hover:bg-ink-800 hover:border-gold-500/30 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              {/* Corner Accent */}
              <div className="absolute top-0 right-0 w-16 h-16 overflow-hidden">
                <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-bl from-gold-500/10 to-transparent transform rotate-45 translate-x-12 -translate-y-12 group-hover:from-gold-500/20 transition-all duration-500" />
              </div>

              {/* Icon */}
              <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-gold-500/20 to-gold-600/10 text-gold-400 rounded-lg mb-6 group-hover:scale-110 transition-transform duration-300">
                {feature.icon}
              </div>

              {/* Content */}
              <h3 className="font-display text-xl font-semibold text-cream-50 mb-3">
                {feature.title}
              </h3>

              <p className="font-body text-cream-400 leading-relaxed mb-4">
                {feature.description}
              </p>

              {/* Accent Text */}
              <div className="flex items-center gap-2 text-gold-400 text-sm font-sans">
                <span className="w-4 h-px bg-gold-500" />
                <span className="italic">{feature.accent}</span>
              </div>

              {/* Hover Line */}
              <div className="absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-gold-500 to-gold-600 w-0 group-hover:w-full transition-all duration-500" />
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className={`text-center mt-16 transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <a
            href="/services"
            className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-gold-500 to-gold-600 text-ink-900 font-sans font-semibold tracking-wide shadow-gold hover:from-gold-400 hover:to-gold-500 transition-all duration-300 group"
          >
            Explore All Features
            <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default ServicesFeatures;
